import React, { useEffect, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Button, { type ButtonProps } from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import {
	ACTION_SUBJECT_PREFIX,
	DEFAULT_GAS_CHANNEL,
	EVENT_SOURCE,
} from '../../constants/analytics';

const styles = cssMap({
	base: {
		paddingTop: token('space.100'),
	},
	buttonText: {
		paddingLeft: token('space.025'),
	},
	contentExpanded: {
		paddingBlock: token('space.100'),
	},
	contentCollapsed: {
		paddingBlock: '0',
	},
});

interface ExpandProps {
	label: string;
	children: React.ReactNode;
	icon: ButtonProps['iconBefore'] | null;
	iconAfter?: (isExpanded: boolean) => ButtonProps['iconAfter'];
	attachmentType: 'file' | 'url' | 'pull-request' | 'comment';
	attachmentCount: number;
}

export function Expand({ children }: ExpandProps): React.JSX.Element {
	return <div>{children}</div>;
}

export const ExpandList = ({
	children,
	defaultExpandedIndex = -1,
	parentPosition,
	buttonType,
	additionalFooterButton,
}: {
	children: React.ReactNode;
	defaultExpandedIndex?: number;
	parentPosition?: number;
	buttonType?: string;
	/**
	 * Optional extra button rendered on the same line as the expand toggles
	 * (e.g. a Summarize button in lite mode). Defaults to nothing so existing
	 * behavior is preserved.
	 */
	additionalFooterButton?: React.ReactNode;
}): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [activeId, setActiveId] = useState<number>(defaultExpandedIndex);

	// Sync activeId when defaultExpandedIndex changes (e.g., when position changes due to filtering)
	useEffect(() => {
		setActiveId(defaultExpandedIndex);
	}, [defaultExpandedIndex]);

	const handleExpand = (expandId: number) =>
		setActiveId((prev) => (prev === expandId ? -1 : expandId));

	const childrenArray = React.Children.toArray(children)
		.filter((child): child is React.ReactElement<ExpandProps> =>
			React.isValidElement<ExpandProps>(child),
		)
		.filter((child) => child.props.attachmentCount > 0);

	return (
		<div>
			<Inline space="space.100">
				{childrenArray.map((child, index) => {
					const isExpanded = activeId === index;
					return (
						<Box key={child.props.label} xcss={styles.base}>
							<Button
								iconBefore={child.props.icon ?? undefined}
								iconAfter={child.props.iconAfter?.(isExpanded)}
								onClick={() => {
									handleExpand(index);
									createAnalyticsEvent({
										action: 'clicked',
										actionSubject: `${ACTION_SUBJECT_PREFIX}ExpandAttachmentButton`,
										eventType: 'ui',
										source: EVENT_SOURCE,
										attributes: {
											attachmentType: child.props.attachmentType,
											attachmentCount: child.props.attachmentCount,
											position: parentPosition,
											buttonType: buttonType,
										},
									}).fire(DEFAULT_GAS_CHANNEL);
								}}
								isSelected={isExpanded}
							>
								<Box xcss={styles.buttonText}>{child.props.label}</Box>
							</Button>
						</Box>
					);
				})}
				{additionalFooterButton && <Box xcss={styles.base}>{additionalFooterButton}</Box>}
			</Inline>
			<Box xcss={styles[activeId >= 0 ? 'contentExpanded' : 'contentCollapsed']}>
				{childrenArray[activeId]}
			</Box>
		</div>
	);
};
